import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Key, Webhook, Plus, Loader2, Trash2, Copy, Play, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ApiWebhooks() {
  const [keys, setKeys] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  // Key dialog
  const [keyOpen, setKeyOpen] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [newKey, setNewKey] = useState(null);
  // Webhook dialog
  const [whOpen, setWhOpen] = useState(false);
  const [whUrl, setWhUrl] = useState('');
  const [whEvents, setWhEvents] = useState([]);
  const [newWebhook, setNewWebhook] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api-publique/keys`),
      axios.get(`${API_URL}/api-publique/webhooks`),
      axios.get(`${API_URL}/api-publique/events`),
    ]).then(([k, w, e]) => { setKeys(k.data); setWebhooks(w.data); setEvents(e.data); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const createKey = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api-publique/keys`, { name: keyName || 'Clé API' });
      setNewKey(res.data);
      toast.success('Clé créée — copiez-la maintenant');
      setKeys((await axios.get(`${API_URL}/api-publique/keys`)).data);
    } catch (e) { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const revokeKey = async (id) => {
    if (!confirm('Révoquer cette clé ?')) return;
    await axios.delete(`${API_URL}/api-publique/keys/${id}`);
    toast.success('Clé révoquée');
    setKeys((await axios.get(`${API_URL}/api-publique/keys`)).data);
  };

  const createWebhook = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api-publique/webhooks`, { url: whUrl, events: whEvents });
      setNewWebhook(res.data);
      toast.success('Webhook créé — copiez le secret');
      setWebhooks((await axios.get(`${API_URL}/api-publique/webhooks`)).data);
    } catch (e) { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const testWebhook = async (id) => {
    try {
      const res = await axios.post(`${API_URL}/api-publique/webhooks/${id}/test`);
      if (res.data.success) toast.success(`Test OK — Status ${res.data.status}`);
      else toast.error(`Échec — ${res.data.error || `Status ${res.data.status}`}`);
    } catch (e) { toast.error('Erreur test'); }
  };

  const deleteWebhook = async (id) => {
    if (!confirm('Supprimer ce webhook ?')) return;
    await axios.delete(`${API_URL}/api-publique/webhooks/${id}`);
    setWebhooks((await axios.get(`${API_URL}/api-publique/webhooks`)).data);
  };

  const toggleEvent = (code) => setWhEvents(prev => prev.includes(code) ? prev.filter(e => e !== code) : [...prev, code]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold"><Key className="w-6 h-6 inline mr-2" />API & Webhooks</h1>

      <Tabs defaultValue="keys">
        <TabsList>
          <TabsTrigger value="keys"><Key className="w-4 h-4 mr-1" /> Clés API</TabsTrigger>
          <TabsTrigger value="webhooks"><Webhook className="w-4 h-4 mr-1" /> Webhooks</TabsTrigger>
          <TabsTrigger value="docs"><ExternalLink className="w-4 h-4 mr-1" /> Documentation</TabsTrigger>
        </TabsList>

        {/* KEYS */}
        <TabsContent value="keys" className="space-y-4">
          <div className="flex justify-between"><p className="text-sm text-slate-500">Clés API pour intégrer Nexus Compta dans vos outils.</p>
            <Button onClick={() => { setKeyOpen(true); setNewKey(null); setKeyName(''); }} className="bg-[#0F5156] hover:bg-[#0d464a]"><Plus className="w-4 h-4 mr-2" /> Nouvelle clé</Button>
          </div>
          <Card><CardContent className="p-0">
            <Table><TableHeader><TableRow><TableHead>Nom</TableHead><TableHead>Clé</TableHead><TableHead>Permissions</TableHead><TableHead>Statut</TableHead><TableHead>Créée le</TableHead><TableHead className="w-20"></TableHead></TableRow></TableHeader>
            <TableBody>
              {keys.map(k => (
                <TableRow key={k.id}>
                  <TableCell className="font-medium">{k.name}</TableCell>
                  <TableCell className="font-mono text-sm text-slate-500">{k.key_prefix}</TableCell>
                  <TableCell>{(k.permissions || []).map(p => <Badge key={p} variant="outline" className="mr-1 text-xs">{p}</Badge>)}</TableCell>
                  <TableCell><Badge className={k.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>{k.is_active ? 'Active' : 'Révoquée'}</Badge></TableCell>
                  <TableCell className="text-sm text-slate-500">{k.created_at?.slice(0, 10)}</TableCell>
                  <TableCell>{k.is_active && <Button variant="ghost" size="sm" onClick={() => revokeKey(k.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>}</TableCell>
                </TableRow>
              ))}
              {keys.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">Aucune clé API</TableCell></TableRow>}
            </TableBody></Table>
          </CardContent></Card>
        </TabsContent>

        {/* WEBHOOKS */}
        <TabsContent value="webhooks" className="space-y-4">
          <div className="flex justify-between"><p className="text-sm text-slate-500">Recevez des notifications en temps réel sur vos URLs.</p>
            <Button onClick={() => { setWhOpen(true); setNewWebhook(null); setWhUrl(''); setWhEvents([]); }} className="bg-[#0F5156] hover:bg-[#0d464a]"><Plus className="w-4 h-4 mr-2" /> Nouveau webhook</Button>
          </div>
          <Card><CardContent className="p-0">
            <Table><TableHeader><TableRow><TableHead>URL</TableHead><TableHead>Événements</TableHead><TableHead>Statut</TableHead><TableHead>Dernier appel</TableHead><TableHead className="w-28">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {webhooks.map(w => (
                <TableRow key={w.id}>
                  <TableCell className="font-mono text-xs max-w-[200px] truncate">{w.url}</TableCell>
                  <TableCell><div className="flex gap-1 flex-wrap">{(w.events || []).slice(0, 3).map(e => <Badge key={e} variant="outline" className="text-[10px]">{e}</Badge>)}{(w.events?.length || 0) > 3 && <Badge variant="outline" className="text-[10px]">+{w.events.length - 3}</Badge>}</div></TableCell>
                  <TableCell><Badge className={w.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}>{w.is_active ? 'Actif' : 'Inactif'}</Badge></TableCell>
                  <TableCell className="text-xs">{w.last_triggered ? `${w.last_status} — ${w.last_triggered.slice(0, 10)}` : '—'}</TableCell>
                  <TableCell><div className="flex gap-1"><Button variant="ghost" size="sm" onClick={() => testWebhook(w.id)}><Play className="w-3 h-3" /></Button><Button variant="ghost" size="sm" onClick={() => deleteWebhook(w.id)}><Trash2 className="w-3 h-3 text-red-500" /></Button></div></TableCell>
                </TableRow>
              ))}
              {webhooks.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-500">Aucun webhook</TableCell></TableRow>}
            </TableBody></Table>
          </CardContent></Card>
        </TabsContent>

        {/* DOCS */}
        <TabsContent value="docs">
          <Card>
            <CardHeader><CardTitle>Documentation API</CardTitle><CardDescription>L'API Nexus Compta est auto-documentée via Swagger/OpenAPI.</CardDescription></CardHeader>
            <CardContent className="space-y-3">
              <Button variant="outline" asChild><a href={`${process.env.REACT_APP_BACKEND_URL}/docs`} target="_blank" rel="noreferrer"><ExternalLink className="w-4 h-4 mr-2" /> Ouvrir Swagger UI</a></Button>
              <Button variant="outline" asChild><a href={`${process.env.REACT_APP_BACKEND_URL}/redoc`} target="_blank" rel="noreferrer"><ExternalLink className="w-4 h-4 mr-2" /> Ouvrir ReDoc</a></Button>
              <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                <p className="text-sm font-semibold mb-2">Authentification API</p>
                <pre className="text-xs bg-slate-900 text-green-400 p-3 rounded-lg overflow-x-auto">
{`curl -H "Authorization: Bearer nxc_votre_cle_api" \\
     ${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api/supplier-invoices`}
                </pre>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Key Dialog */}
      <Dialog open={keyOpen} onOpenChange={setKeyOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouvelle clé API</DialogTitle></DialogHeader>
          {newKey ? (
            <div className="space-y-4">
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <p className="text-sm font-bold text-amber-700 mb-2">Copiez cette clé maintenant — elle ne sera plus affichée.</p>
                <div className="flex gap-2"><Input value={newKey.key} readOnly className="font-mono text-sm" /><Button variant="outline" onClick={() => { navigator.clipboard.writeText(newKey.key); toast.success('Copié'); }}><Copy className="w-4 h-4" /></Button></div>
              </div>
              <Button onClick={() => setKeyOpen(false)} className="w-full">Fermer</Button>
            </div>
          ) : (
            <>
              <div><Label>Nom de la clé</Label><Input value={keyName} onChange={e => setKeyName(e.target.value)} placeholder="Mon intégration" /></div>
              <DialogFooter><Button variant="outline" onClick={() => setKeyOpen(false)}>Annuler</Button><Button onClick={createKey} disabled={saving} className="bg-[#0F5156] hover:bg-[#0d464a]">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Créer</Button></DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Webhook Dialog */}
      <Dialog open={whOpen} onOpenChange={setWhOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Nouveau webhook</DialogTitle></DialogHeader>
          {newWebhook ? (
            <div className="space-y-4">
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <p className="text-sm font-bold text-amber-700 mb-2">Copiez ce secret maintenant.</p>
                <div className="flex gap-2"><Input value={newWebhook.secret} readOnly className="font-mono text-sm" /><Button variant="outline" onClick={() => { navigator.clipboard.writeText(newWebhook.secret); toast.success('Copié'); }}><Copy className="w-4 h-4" /></Button></div>
              </div>
              <Button onClick={() => setWhOpen(false)} className="w-full">Fermer</Button>
            </div>
          ) : (
            <>
              <div><Label>URL</Label><Input value={whUrl} onChange={e => setWhUrl(e.target.value)} placeholder="https://votre-app.com/webhook" /></div>
              <div><Label>Événements</Label>
                <div className="grid grid-cols-2 gap-2 mt-2 max-h-48 overflow-y-auto">
                  {events.map(e => (
                    <label key={e.code} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox checked={whEvents.includes(e.code)} onCheckedChange={() => toggleEvent(e.code)} />
                      {e.label}
                    </label>
                  ))}
                </div>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setWhOpen(false)}>Annuler</Button><Button onClick={createWebhook} disabled={saving || !whUrl} className="bg-[#0F5156] hover:bg-[#0d464a]">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Créer</Button></DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
